import { FC, useContext } from "react";

import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { TranslationContext } from "@/app/providers";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from "@mui/material";

import { CloseActionButton } from "./AppActionButtons";

interface ImageAttachmentPreview {
	url: string;
	fileName: string;
}

interface ImageAttachmentPreviewDialogProps {
	preview: ImageAttachmentPreview | null;
	onClose: () => void;
}

export const ImageAttachmentPreviewDialog: FC<
	ImageAttachmentPreviewDialogProps
> = ({ preview, onClose }) => {
	const translation = useContext(TranslationContext);

	return (
		<Dialog open={Boolean(preview)} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle
				sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
			>
				<Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }}>
					{preview?.fileName || (
						<Translate String="Image.Preview.Title">Image preview</Translate>
					)}
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(
						translation,
						"Image.Preview.Close",
						"Close image preview",
					)}
					onClick={onClose}
					size="small"
					sx={{ position: "absolute", right: 12, top: 12 }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{preview ? (
					<Box
						component="img"
						src={preview.url}
						alt={preview.fileName}
						sx={{
							width: "100%",
							maxHeight: "75vh",
							objectFit: "contain",
							borderRadius: 1,
							display: "block",
						}}
					/>
				) : null}
			</DialogContent>
			<DialogActions>
				<CloseActionButton onClick={onClose}>
					<Translate String="Common.Close">Close</Translate>
				</CloseActionButton>
				<Button
					component="a"
					href={preview?.url ?? "#"}
					target="_blank"
					rel="noopener noreferrer"
					variant="outlined"
					startIcon={<OpenInNewIcon fontSize="small" />}
					disabled={!preview?.url}
				>
					<Translate String="Common.OpenInNewTab">Open in new tab</Translate>
				</Button>
				<Button
					component="a"
					href={preview?.url ?? "#"}
					download={preview?.fileName || "image"}
					target="_blank"
					rel="noopener noreferrer"
					variant="contained"
					startIcon={<DownloadIcon fontSize="small" />}
					disabled={!preview?.url}
				>
					<Translate String="Image.Preview.Save">Save image</Translate>
				</Button>
			</DialogActions>
		</Dialog>
	);
};
